import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useBrowserRoute } from '../router/useBrowserRoute.ts';
import { parsePath, buildPath, type Workspace } from '../router/parsePath.ts';
import { useAuth } from '../hooks/useAuth.ts';
import { useRobots, type Robot } from '../hooks/useRobots.ts';
import { useStats } from '../hooks/useStats.ts';
import { usePendingCommands } from '../hooks/useCommands.ts';
import { duration, num, NO_VALUE } from '../components/format.ts';
import { StatusDot } from '../components/StatusDot.tsx';
import { type Tone } from '../components/state.ts';
import { Icon, type IconName } from './Icon.tsx';
import {
  TopBar,
  fleetHeadline,
  isTypingTarget,
  useFeedWord,
  useLiveHold,
  useSupply,
  useSurfaceRead,
  type SurfaceRead,
} from './TopBar.tsx';
import { Surface } from '../components/states/index.ts';
import { FleetWorkspace } from '../workspaces/fleet/FleetWorkspace.tsx';
import { CommandsWorkspace } from '../workspaces/commands/CommandsWorkspace.tsx';
import { CatalogWorkspace } from '../workspaces/catalog/CatalogWorkspace.tsx';
import { TelemetryWorkspace } from '../workspaces/telemetry/TelemetryWorkspace.tsx';
import styles from './AppShell.module.css';

/* ==========================================================================
   AppShell — the frame every route hangs in.

   A ceiling, a floor, one rail, and nothing else:

     34px  TOP BAR       who / where / WHEN, for the whole console — identity,
                         environment, the fleet pulse, the command queue, ⌘K,
                         the one transport readout and a Zulu clock. Every
                         global answer to "is this now" lives up there.
     36px  SURFACE STRIP who / what / how current, for THIS surface — see the
                         three-zone contract below.
     48px  NAV RAIL      the four workspaces, each carrying the number an
                         operator navigates on
     22px  STATUS BAR    the floor. Ambient transport, tenant and host on the
                         left; the surface's volume, its cadence and the
                         ABSOLUTE Zulu stamp of its last good read on the
                         right. See THE FLOOR, below.

   all four grounded on --bg-0 so the chrome reads as one continuous dark
   surface and every panel of data (--bg-1) is a step above it. `main` is the
   only elastic row in the grid: it fills from the top bar's hairline to the
   floor's, so no route can end in a field of bare ground.

   ---------------------------------------------------------------------------
   THE SURFACE STRIP'S THREE-ZONE CONTRACT
   ---------------------------------------------------------------------------
   Every route gets the same 36px band and every route fills it. It used to
   carry 300px of tabs in a 1552px band — 81% of an instrument's header spent
   on nothing — and it vanished entirely on /commands, so two of the four
   workspaces drew a second identity band of their own underneath.

   The three zones are `auto | 1fr | auto`: identity and provenance are one
   left-packed run separated by a hairline, and the flexible space is all
   spent before the freshness-and-verb cluster at the right edge.

     IDENTITY (left, at the content column's own text edge)
       `Fleet › [Operations 13/28] [Robot list 28] [Map 27/28]`
       `Telemetry › Signal explorer › [Pathfinder 01 ⌄] rover-01`

       The workspace root, then the surface. Where the workspace has sibling
       surfaces the leaf is the tab group — a breadcrumb whose last element you
       can change is a tab strip, and drawing both would be drawing the same
       fact twice. Where it has ONE surface the leaf is a plain label: an
       accent underline under a tab with no siblings marks nothing.

       Where the surface is scoped to a single object, the object's name and id
       are the next crumb — and the picker that changes it sits IN that crumb,
       not 1350px away at the other bezel. The picker is the page's own control
       (the shell has no way to set page state); the shell moves it into the
       identity zone and re-dresses it as a crumb. See the hoist, below.

     CONTROLS / PROVENANCE (centre)
       `28 hardware · 4 morphologies · 5 builds · 3 sites   /api/robots`

       The surface's own filter and segment controls belong here, hard against
       the identity they qualify. Not one of the four workspaces currently
       exposes any — see the report — so the track's resting content is what
       the surface is showing and where those numbers came from. §6 of the bar
       asks for provenance beside the reading ("which robot, which agent
       version, simulated-vs-real, live stream or recording") and for an
       operator auditing a figure the endpoint is exactly that. Every figure
       here is read off the same query cache that painted the surface, so the
       line cannot drift from it.

       Left-packed against the identity rather than centred: the run reads as
       one sentence left to right, and it cannot move when a verb appears on
       the far side — a reading that shifts when a button arrives is a reading
       the operator has to find again.

       Nothing in this band moves for data either. Every figure is width-
       reserved (`.factValue` holds two digits; a tab's fraction is padded with
       U+2007 so ` 0/28` is `13/28`'s box), the robot picker is a fixed-width
       crumb rather than one sized to whichever robot is selected, and the
       freshness word is reserved to `Polling off` so the switch flipping cannot
       nudge the endpoint or the re-read button. Below 1360px the labels
       NARROW — `28 hw · 4 morph · 5 bld · 3 sites` — they never leave: a unit
       always carries its number.

     FRESHNESS + VERB (right)
       `((•)) Live   read 3s ago ⟳   [+ New robot]`

       Tell #10 and §6: the operator must never have to ask "is this now".
       They are answered at three scopes and this is the middle one — the top
       bar answers it for the LINK, a panel header answers it for a PANEL, and
       this answers it for the SURFACE the strip heads: the stalest of the
       sources this screen is drawn from, and whether they are still arriving.

       `Live` is a switch, not a light. Pressing it holds every poll in the
       console (TopBar's `useLiveHold` — TanStack's own focus gate, not a
       second timer) so a table stops re-sorting under an operator who is
       reading row 40, and the age keeps counting so they can see exactly how
       far behind that put them. `Go Live` in `palantir-gotham-facet-rail-
       timeline.png`, the greying `LIVE` pill in `formant-timeline-data-
       presence-scrubber.png`, and InOrbit's per-panel `Live` ⇄ `Live off` are
       the same control in three products.

       Then the surface's one primary action — `+ New robot` on /fleet/list —
       pinned to the bezel. Empty where a surface has none, which is the right
       shape: §6 grants exactly one primary per surface, not at least one.

   ---------------------------------------------------------------------------
   THE FAILURE STATE IS DESIGNED, NOT DEFAULTED
   ---------------------------------------------------------------------------
   A console is judged on the screen it shows when the platform is down, and
   the last round's was three separate lies:

     · the account control UNMOUNTED, taking identity, tenant, role, host and
       the only theme switch in the build with it, at the exact moment an
       operator needs to know whether the problem is the platform or them;
     · a dead platform escalated to a 6px dot and the word `Failing` in an
       80px control at the far bezel — the same footprint a healthy surface
       spends saying `Live`;
     · and the workspace under it drew `Status (0)`, five zeroed bars and the
       line `Every robot is inside its limits.` about 28 machines the console
       had never spoken to.

   All three are fixed at the root, and there is one computation behind them.
   `useSurfaceRead` (TopBar) reports a `fault` for the surface's first failing
   source — endpoint, error verbatim, consecutive-run length, and the last
   good read as an absolute Zulu stamp — plus `unread`, which is true when
   NOTHING the surface is drawn from has ever landed. The shell reads it once
   here and spends it three ways:

     THE STRIP    zones 2 and 3 are replaced by a full-bleed --error row —
                  `/api/robots — Failed to fetch · 6 consecutive · last good
                  read 16:59:02Z · and 1 more source failing   [Retry]`. The
                  run length distinguishes a blip from an outage and the
                  trailing count distinguishes one bad endpoint from a dead
                  platform, which are three different call-outs. See
                  StripFault.
     THE CANVAS   when `unread`, the workspace's picture is covered by one
                  statement of what is missing and why, then by TWO dense
                  tables side by side: every source this surface is mounted
                  against with its cadence, its state and the Zulu stamp of
                  its last good read — and beside it the console's own vitals,
                  not one of which comes from the API. The browser's network
                  verdict, the host, the plane, the request counters this
                  document measured on itself. The first question of an outage
                  is "is it me or is it them", and a screen that can only
                  answer half of it sends the operator to a terminal. See
                  NoData and Probe.
     THE DOM      `data-nodata` and `data-failing="<source>"` land on <main>,
                  so a workspace can answer the same fact per panel — which is
                  the only place the "/api/stats is down but /api/robots is
                  fine" case can be answered properly.

   The one thing that made all of this hard to get right is in `isFailing`
   (TopBar): query-core clears `error` and returns `status` to `'pending'` at
   the top of every fetch on a query with no data, so the obvious flags drop
   out twice a poll and the whole failure state flickers. Nothing in the shell
   reads `isError`; everything reads which terminal event happened last.

   ---------------------------------------------------------------------------
   WHY THE RAIL IS 48px AND HAS NO WIDE STATE
   ---------------------------------------------------------------------------
   It used to be 152px of four bare words with 860px of nothing under them.
   Every dark reference in the set runs its primary nav as a 40–56px icon rail
   (Anduril Lattice, Gotham, Foxglove) and spends the width it saves on data;
   the only wide left rails in the set carry workspace content — facets, a
   settings tree, a blueprint — not destinations. With four destinations there
   is no width to earn, so the rail is icons at 48px, permanently, and each
   cell carries the reading an operator navigates on.

   Beyond the layout, the shell owns:

     · The viewport contract. The document never scrolls. The shell is exactly
       one viewport tall and every scroller inside it is a panel body, so a
       header can never be pushed off the top and a table can never take the
       whole page with it.
     · The content column. One text edge down the whole console — the strip's
       first glyph, the first panel title and the first table header all land
       on it. See `--strip-inset` and the seam assertion in AppShell.module.css.
     · The browser tab. The tab title and the strip's leaf must agree about
       what the operator is looking at; both derive from the same surface list.
     · The keyboard entry point. Skip-to-content, ⌘K (owned by TopBar), ↑/↓
       down the nav rail and ←/→ across the surface tabs.
   ========================================================================== */

/* --------------------------------------------------------------------------
   Surfaces and strips.
   -------------------------------------------------------------------------- */

interface Surface {
  key: string;
  label: string;
  path: string;
  /** Printed after the label in mono. Null when the surface has no count. */
  count: string | null;
  /**
   * The tone the NUMERATOR of that count is painted in.
   *
   * Only ever set where the count is one of the console's two fleet readings
   * — `out of limits` is amber wherever it appears, so the Operations tab's
   * `13/28` is the same colour as the top bar's, the nav rail's and the KPI
   * card's. A count that is merely a cardinality (`28` robots, `27/28`
   * located) stays neutral and takes the tab's own colour; painting those
   * would be colour that does not mean anything (§8, tell 9).
   */
  countTone?: Tone;
  /** What that count counts, spelled out. */
  countTitle: string;
  /** What the browser tab says when this surface is active. */
  doc: string;
}

/** One `28 robots` cell of the provenance line. */
interface Fact {
  value: string;
  label: string;
  /**
   * The short spelling the label narrows to below 1260px — `hardware` → `hw`,
   * `morphologies` → `morph`. Never a way to DROP the label: a unit always
   * carries its number, so `28 4 5 3` is not a state this line has. Absent
   * means the word is already short enough to survive at every width.
   */
  abbr?: string;
  /**
   * A fact whose value IS its own name — `hardware`, `sim`. Rendered as one
   * outlined token rather than as a labelled figure, which is
   * `anduril-lattice-asset-detail-tasking.png`'s `Simulated` chip and §5's
   * level 6: a chip is legitimate for a singular, page-level fact, and
   * "is the thing I am looking at real" is the most singular fact there is.
   * Outlined, never filled — the filled-badge budget belongs to state.
   */
  token?: boolean;
}

interface Strip {
  aria: string;
  /** Which builder assembles the volatile half of the line. */
  kind: Workspace;
  root: { label: string; path: string };
  surfaces: Surface[];
  activeKey: string;
  /** The surface is scoped to one robot. */
  objectScope: 'telemetry-robot' | null;
  /**
   * WHICH robot — read off the route, not sniffed out of the query cache.
   *
   * The strip's picker, the endpoint it prints and the canvas under it all
   * read this one value, so the band and the table can never be showing two
   * different robots. It is in the URL because a signal an operator wants a
   * second pair of eyes on has to be a link they can paste.
   */
  objectId: string | null;
  /**
   * Where the page frame's own action slot is moved to.
   *   left   it is the object picker and belongs in the identity crumb
   *   right  it is the surface's one verb and belongs beside the freshness
   *   none   the page's only verb is a link the strip now carries as a tab
   */
  hoist: 'none' | 'left' | 'right';
  /** Endpoints behind this surface, printed in the provenance line. */
  source: string;
  /**
   * The `TopBar.SOURCES` ids this surface's picture is drawn from. The right
   * zone reports the STALEST of them — a surface is exactly as current as its
   * least current input, and quoting the freshest would be flattering.
   */
  sourceIds: readonly string[];
}

/* Every view the fleet router knows (router/useFleetRoute). Anything else in
   that position is not a view at all, and the router falls through to the
   dashboard — the strip has to fall the same way or the frame and the canvas
   would disagree about where you are. */
const FLEET_VIEWS = new Set(['dashboard', 'list', 'map', 'detail', 'scan', 'teleop']);
/* …and these three are the ones the shell can name completely. A robot detail
   page, a teleop console and a scan viewer are a single object's own surface:
   there the page's 20px title IS the object's name, it carries its own live
   state beside it, and the shell has nothing truer to say than the page does.

   `teleop` was missing from the set above, which is a different bug with the
   same cause: an unknown view falls through to `dashboard`, so the teleop
   console rendered the workspace strip with `Operations` marked as the
   current tab — the shell asserting, in accent, that the operator was on a
   surface they were not. Three ways to be inside the fleet workspace became
   two, and the two are now a rule: a WORKSPACE surface wears the strip, an
   OBJECT's own surface wears a 40px head with a crumb back to the workspace. */
const FLEET_STRIP_VIEWS = new Set(['dashboard', 'list', 'map']);

function fleetSurfaces(robots: readonly Robot[], unread: boolean): Surface[] {
  /* A fleet we have not read prints em dashes, never zeroes: "no robots need
     attention" and "we have not been able to ask" are different facts and only
     one of them is good news. Each placeholder is the shape of the value it
     stands in for, so the tabs do not move when the read lands. */
  const { total } = fleetHeadline(robots);
  const located = robots.filter((r) => r.lat != null && r.lon != null).length;
  /* Padded to the denominator's own width with U+2007 FIGURE SPACE, which is
     exactly one tabular digit wide: `13/28` and ` 0/28` are the same box, so a
     fleet recovering does not slide the provenance line beside it. A CSS
     min-width would have put the slack between the label and its count. */
  const pad = (n: number, width: number) => String(n).padStart(width, '\u2007');
  const digits = String(total).length;
  const frac = (n: number) =>
    unread ? `${NO_VALUE}/${NO_VALUE}` : `${pad(n, digits)}/${total}`;
  const whole = (n: number) => (unread ? NO_VALUE : String(n));

  return [
    {
      key: 'dashboard',
      /* NO COUNT, deliberately. This tab used to print `13/28` — character
         for character the reading the top bar carries and the reading the
         `NEEDS ATTENTION 13/28` card 90px below it carries. A tab count says
         how many rows are behind the tab; Operations is not a list, it is the
         whole fleet drawn six metrics deep, and the one figure it could have
         printed already has a home in the chrome. `Robot list 28` and
         `Map 27/28` keep theirs because those two ARE lists and their counts
         differ from each other. */
      label: 'Operations',
      path: buildPath('fleet'),
      count: null,
      countTitle: '',
      doc: 'Operations',
    },
    {
      key: 'list',
      label: 'Robot list',
      path: buildPath('fleet', 'list'),
      count: whole(total),
      countTitle: unread ? 'Fleet not read' : `${total} robots registered`,
      doc: 'Robot list',
    },
    {
      key: 'map',
      label: 'Map',
      path: buildPath('fleet', 'map'),
      count: frac(located),
      countTitle: unread
        ? 'Fleet not read'
        : `${located} of ${total} robots are reporting a position fix`,
      doc: 'Fleet map',
    },
  ];
}

/**
 * Which robot /telemetry is showing.
 *
 * The route says, and the fleet's first robot stands in until it does — so a
 * bare `/telemetry` still lands on a signal rather than on an empty frame,
 * and picking a robot pushes `/telemetry/<id>`. One function, read by the
 * strip (for its picker and its endpoint) and by the canvas, because two
 * answers to "which robot" is the whole class of bug this console keeps
 * finding in itself.
 */
function telemetryRobotOf(
  segments: readonly string[],
  robots: readonly Robot[],
): string | null {
  const id = segments[0];
  /* Before the fleet read lands there is nothing to validate against, and
     refusing the id would throw away the one thing the URL knows. */
  if (id !== undefined && (robots.length === 0 || robots.some((r) => r.robot_id === id))) {
    return id;
  }
  return robots[0]?.robot_id ?? null;
}

function stripFor(
  workspace: Workspace,
  segments: readonly string[],
  robots: readonly Robot[],
  unread: boolean,
): Strip | null {
  switch (workspace) {
    case 'fleet': {
      const head = segments[0];
      const view = head !== undefined && FLEET_VIEWS.has(head) ? head : 'dashboard';
      if (!FLEET_STRIP_VIEWS.has(view)) return null;
      return {
        aria: 'Fleet surfaces',
        kind: 'fleet',
        root: { label: 'Fleet', path: buildPath('fleet') },
        surfaces: fleetSurfaces(robots, unread),
        activeKey: view,
        objectScope: null,
        objectId: null,
        /* Operations' only verb was `All robots →`, a one-way link the strip
           now carries as a tab; the list and the map own real verbs. */
        hoist: view === 'dashboard' ? 'none' : 'right',
        source: view === 'dashboard' ? '/api/robots · /api/stats' : '/api/robots',
        sourceIds: view === 'dashboard' ? ['robots', 'stats'] : ['robots'],
      };
    }
    case 'telemetry': {
      const scoped = telemetryRobotOf(segments, robots);
      const named = scoped === null
        ? null
        : (robots.find((r) => r.robot_id === scoped)?.name ?? scoped);
      return {
        aria: 'Telemetry surface',
        kind: 'telemetry',
        root: { label: 'Telemetry', path: buildPath('telemetry') },
        surfaces: [
          {
            key: 'signals',
            label: 'Signal explorer',
            path: buildPath('telemetry'),
            count: null,
            countTitle: '',
            /* The tab names the ROBOT, not just the surface: this view is
               scoped to one machine, and six tabs all reading
               "Signal explorer" is a tab strip that has stopped helping.
               Same rule as /fleet/detail, and the same reason it looks the
               name up rather than printing the slug. */
            doc: named === null ? 'Signal explorer' : `${named} · Signal explorer`,
          },
        ],
        activeKey: 'signals',
        objectScope: 'telemetry-robot',
        objectId: scoped,
        /* The shell draws this route's canvas, so it draws this route's
           picker too — natively, in the crumb, instead of collapsing a page
           frame's identity band in CSS and flying its <select> into the
           strip. That hoist still exists for the three surfaces this piece
           does not own; it no longer exists here. */
        hoist: 'none',
        source: '/api/robots/…/telemetry',
        sourceIds: ['robots', 'telemetry'],
      };
    }
    case 'commands':
      return {
        aria: 'Command surfaces',
        kind: 'commands',
        root: { label: 'Commands', path: buildPath('commands') },
        surfaces: [
          {
            key: 'queue',
            label: 'Queue',
            path: buildPath('commands'),
            count: null,
            countTitle: '',
            doc: 'Command queue',
          },
        ],
        activeKey: 'queue',
        objectScope: null,
        objectId: null,
        hoist: 'right',
        source: '/api/commands/pending',
        sourceIds: ['commands'],
      };
    case 'catalog':
      return {
        aria: 'Catalog surfaces',
        kind: 'catalog',
        root: { label: 'Catalog', path: buildPath('catalog') },
        surfaces: [
          {
            key: 'revisions',
            label: 'Hardware revisions',
            path: buildPath('catalog'),
            count: null,
            countTitle: '',
            doc: 'Hardware revisions',
          },
        ],
        activeKey: 'revisions',
        objectScope: null,
        objectId: null,
        hoist: 'right',
        source: '/api/morphologies',
        sourceIds: ['catalog'],
      };
  }
}

/* What the browser tab says when there is no strip to derive it from. A tab
   reading "F3 Fleet" over a page reading "Signal Explorer" is a small lie the
   operator pays for every time they have six tabs open on six robots — and so
   is a tab reading `jetrover-01` over a page reading `Pathfinder 01`, which is
   why the robot's own name is looked up here rather than assumed from the
   slug in the URL. */
function fallbackTitle(
  workspace: Workspace,
  segments: readonly string[],
  robots: readonly Robot[],
): string {
  switch (workspace) {
    case 'fleet': {
      const [view, id] = segments;
      if (view === 'detail' && id) {
        return robots.find((r) => r.robot_id === id)?.name ?? id;
      }
      if (view === 'scan' && id) return `Scan ${id}`;
      return 'Fleet';
    }
    case 'telemetry':
      return 'Telemetry';
    case 'commands':
      return 'Commands';
    case 'catalog':
      return 'Catalog';
  }
}

/* ==========================================================================
   Nav rail — 48px, five glyphs, and NO NUMBERS.

   It used to print a count under every glyph: `13/28` under Fleet, `16` under
   Commands, `15.7k` under Telemetry, `4` under Catalog. Two of those four were
   the top bar's own readings, repeated 40px below the bar that owns them, and
   `13/28` was on screen four times in one frame of /fleet — bar, rail, tab
   strip and KPI card. A console earns trust by saying each thing once.

   So the rail is what §2 says a left icon rail is in every dark reference in
   the set (`anduril-lattice-*`, `palantir-gotham-task-queue-globe.png`): a
   40–56px column of glyphs where the only state is WHICH ONE IS CURRENT.
   "Is anything wrong over there" is answered by the top bar's rollup, which
   is on screen on every route; the rail answers "where am I".

   Each cell still carries the workspace's own reading in its title and its
   screen-reader label — the fleet's two nouns, the queue depth, the telemetry
   volume — because that costs no pixels and no duplication.

   THE ONE CELL THAT IS NOT A WORKSPACE is the robot list. Every other rail
   destination is a workspace root, but `/fleet/list` is the fleet's ledger —
   the surface an operator goes to by name, the one the palette lists as `2`,
   and the only way to reach it was the Operations tab strip, which exists on
   three of the console's routes and on none of the other three. A destination
   that has its own digit and its own noun and cannot be reached from the
   chrome that is on screen everywhere is not linked, it is nested. So the rail
   carries it, and `Fleet` stops claiming it — an item is current when the
   route is ITS surface, never when the route is merely inside its workspace.
   ========================================================================== */

interface NavItem {
  /** Stable react key; also the surface's own name where it has one. */
  key: string;
  path: string;
  /** Whether the route on screen IS this cell's surface. */
  active: boolean;
  label: string;
  icon: IconName;
  detail: string;
}

function NavRail({
  current,
  view,
  onNavigate,
}: {
  current: Workspace;
  /** First path segment inside the workspace — `''` at a workspace root. */
  view: string;
  onNavigate: (path: string) => void;
}) {
  const ref = useRef<HTMLElement>(null);
  const { data: robotData } = useRobots();
  const { data: stats } = useStats();

  const robots = useMemo(() => robotData ?? [], [robotData]);
  const head = useMemo(() => fleetHeadline(robots), [robots]);

  const items = useMemo<NavItem[]>(() => {
    const unread = robotData === undefined;
    return [
      {
        key: 'fleet',
        path: buildPath('fleet'),
        /* NOT `current === 'fleet'`. The fleet workspace holds six views and
           two of them are rail cells now; a parent that stays lit while a
           child is lit puts the accent in two places and stops answering
           "where am I". Every fleet view that is NOT the list — operations,
           map, a robot's own page, teleop, a scan — belongs to this cell,
           because those are the surfaces you reach through Operations. */
        active: current === 'fleet' && view !== 'list',
        label: 'Fleet',
        icon: 'fleet',
        detail: unread
          ? 'fleet not read'
          : `${head.total} robots · ${head.flagged} with a reading outside its limits` +
            (head.faulted > 0 ? `, ${head.faulted} refusing motion` : ''),
      },
      {
        key: 'list',
        path: buildPath('fleet', 'list'),
        active: current === 'fleet' && view === 'list',
        label: 'Robot list',
        icon: 'list',
        /* The ledger's own count, and the same sentence the Robot list tab
           puts in its count title — one wording for one number. */
        detail: unread ? 'fleet not read' : `${head.total} robots registered`,
      },
      {
        key: 'telemetry',
        path: buildPath('telemetry'),
        active: current === 'telemetry',
        label: 'Telemetry',
        icon: 'telemetry',
        /* The count is nullable: a deployment whose telemetry lives outside
           Postgres reports no 24 h figure at all (see useStats). The rail
           runs OUTSIDE every RouteBoundary — the shell is what survives a
           route fault — so an unguarded read here is not a broken line, it
           is the whole console gone to a blank page. */
        detail:
          stats === undefined
            ? 'not read'
            : stats.telemetry_last_24h === null
              ? '24 h ingest volume not reported'
              : `${num(stats.telemetry_last_24h)} readings ingested in the last 24 h`,
      },
      {
        key: 'commands',
        path: buildPath('commands'),
        active: current === 'commands',
        label: 'Commands',
        icon: 'commands',
        /* `unacked`, the same word and the same set as the top bar's chip —
           `pending + sent` off /api/commands/pending. NOT the Commands panel's
           `Queue 26`, which is a wider set off a different endpoint. */
        detail:
          stats === undefined
            ? 'not read'
            : `${stats.pending_commands} issued and not yet acknowledged`,
      },
      {
        key: 'catalog',
        path: buildPath('catalog'),
        active: current === 'catalog',
        label: 'Catalog',
        icon: 'catalog',
        detail: 'morphologies and their hardware revisions',
      },
    ];
  }, [robotData, head, stats, current, view]);

  /* ↑/↓ walk the rail. Focus only — changing workspace is a navigation and
     stays a deliberate press, never a side effect of looking. */
  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    const cells = Array.from(ref.current?.querySelectorAll<HTMLElement>('[data-nav]') ?? []);
    const i = cells.indexOf(document.activeElement as HTMLElement);
    if (i < 0 || cells.length === 0) return;
    e.preventDefault();
    const next =
      e.key === 'ArrowDown' ? (i + 1) % cells.length : (i - 1 + cells.length) % cells.length;
    cells[next]?.focus();
  }, []);

  return (
    <nav className={styles.rail} aria-label="Workspaces" ref={ref} onKeyDown={onKeyDown}>
      {items.map((item) => {
        const { active } = item;
        return (
          <button
            key={item.key}
            type="button"
            data-nav="true"
            className={styles.navCell}
            data-active={active}
            aria-current={active ? 'page' : undefined}
            aria-label={`${item.label} — ${item.detail}`}
            title={`${item.label} · ${item.detail}`}
            onClick={() => onNavigate(item.path)}
          >
            <span className={styles.navGlyph}>
              <Icon name={item.icon} size={16} />
            </span>
          </button>
        );
      })}
    </nav>
  );
}

/* ==========================================================================
   The surface strip.
   ========================================================================== */

/**
 * Re-render on every query-cache event.
 *
 * The strip reads the cache directly rather than through `useQuery`, because
 * what it needs is not one query's data but the shape of a whole SET of
 * queries — which of them are mounted, when each last landed, whether any of
 * them failed. Deliberately scoped to this component: subscribing the whole
 * shell to the cache would re-render every workspace, and one of them owns a
 * three.js viewport.
 */
function useCacheTick(qc: QueryClient): number {
  const [tick, bump] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    /* Coalesced and deferred out of the render phase. A workspace mounting its
       queries emits cache events WHILE it renders, and bumping synchronously
       there is a setState-during-render — React logs it, and in a console that
       polls five queries it would log it on every navigation. One timeout per
       burst also collapses "28 robots landed" into a single re-render. */
    let queued = 0;
    const unsubscribe = qc.getQueryCache().subscribe(() => {
      if (queued !== 0) return;
      queued = window.setTimeout(() => {
        queued = 0;
        bump();
      }, 0);
    });
    return () => {
      if (queued !== 0) window.clearTimeout(queued);
      unsubscribe();
    };
  }, [qc]);
  return tick;
}

function countDistinct(robots: readonly Robot[], pick: (r: Robot) => string | null): number {
  const seen = new Set<string>();
  for (const r of robots) {
    const v = pick(r);
    if (v) seen.add(v);
  }
  return seen.size;
}

function metaString(r: Robot, key: string): string | null {
  const v = r.meta[key];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

/**
 * The fleet's size, told in the terms that answer "is this real?".
 *
 * §6 of the bar wants simulated-vs-real declared beside the reading, and the
 * API declares it per robot as `meta.kind`. Where every robot carries one, the
 * headline count IS the kind breakdown — `28 hardware`, or `24 hardware · 4
 * sim` on a mixed fleet — so one figure answers both how many and how real.
 * Where the field is missing on any robot the console will not guess: it falls
 * back to a bare `28 robots`. The words are the API's own, never invented.
 */
/* The API's own words, shortened — never renamed. A kind this table does not
   know keeps its full spelling at every width, which costs pixels and cannot
   cost meaning. */
const KIND_ABBR: Record<string, string> = { hardware: 'hw', sim: 'sim' };

function fleetSizeFacts(robots: readonly Robot[]): Fact[] {
  const byKind = new Map<string, number>();
  for (const r of robots) {
    const k = metaString(r, 'kind');
    if (k === null) return [{ value: String(robots.length), label: 'robots' }];
    byKind.set(k, (byKind.get(k) ?? 0) + 1);
  }
  if (byKind.size === 0) return [{ value: String(robots.length), label: 'robots' }];
  return [...byKind.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([kind, n]) => ({ value: String(n), label: kind, abbr: KIND_ABBR[kind] }));
}

/* --------------------------------------------------------------------------
   `((•)) Live   read 3s ago ⟳` — the freshness of the surface the strip heads.

   Two controls, in this order, because the second qualifies the first:

     the SWITCH   Polling ⇄ Polling off (`Live` while push is up — the word is
                  the transport's, see `useFeedWord`). A hold stops every poll at
                  TanStack's own focus gate. Amber when held, because the
                  operator is looking at a picture that is no longer being
                  refreshed and that is worth one step of contrast — but never
                  red: holding is a decision, not a fault (§3).

                  A surface nothing polls (the catalog) reads `On demand` with
                  a hollow grey dot and the switch disabled, carrying its
                  reason. Three states, three renderings — a document must not
                  look like a feed somebody paused.

     the AGE      The stalest source's age, and a re-read. Escalates on its own
                  evidence: amber past three cadences, red if a read failed.
                  Pressing it re-reads even while held, which is the whole
                  point of a hold — stop the churn, take a reading when you
                  want one.
   -------------------------------------------------------------------------- */

function SurfaceLive({ strip, read }: { strip: Strip; read: SurfaceRead }) {
  const { held, setHeld } = useLiveHold();
  /* The word is the transport's, not this widget's — see `useFeedWord`. The
     eight panel headers under this strip name the same fact, and until this
     line took the shared word they named it differently: `● Live` here,
     `● Polling 5s` 100px below, one dot each. */
  const feedWord = useFeedWord();

  /* Five states, five words, and `Live` is never one of them while a read is
     failing. A surface that says `Live` over data that stopped arriving is the
     exact lie tell #10 exists to catch — the age beside it going red is not
     enough on its own, because the word is what gets read first.

     `held` is separated from `off` because the two have different causes and
     an operator acts differently on each: `Held` is a switch THIS operator
     threw and can throw back, `Live off` is the console reporting that it
     stopped being current for a reason nobody chose. Collapsing them —
     which is what the band did until now — makes the one recoverable state
     look like the one that needs investigating.

     It is also the word this console uses for the state everywhere else:
     `HOLD` in the top bar's mode pill, `held` on every panel chip below (see
     THE HOLD, PROPAGATED in AppShell.module.css), `polling held` in the
     floor. One state, one root word, four bands.

     `failing` should be unreachable: the strip swaps this whole cluster for
     the fault row the moment `read.fault` is non-null, and that is the same
     condition as `tone === 'error'`. It is kept as a guard rather than
     removed — if those two ever drift apart, the switch still refuses to say
     `Live`, which is the property that matters. */
  /* A ZERO IS A MEASUREMENT. AN UNREAD SOURCE IS NOT — and neither is a
     liveness word. `unread` is the sixth state and it sits ABOVE `live`
     deliberately: nothing this surface is drawn from has ever answered, so
     there is no picture for `Polling` to describe. It used to be reachable
     only through `live`, which asked whether a timer was mounted rather than
     whether anything had landed, and a hung API therefore flew `● Polling`
     with a filled dot over `0 registered` and an empty table. `Reading` while
     a request is genuinely in flight, `No reading` when there is not one —
     the same two words, in the same order, that `ReadTag` prints in every
     panel header under this strip. */
  const state = read.tone === 'error'
    ? 'failing'
    : read.onDemand
      ? 'ondemand'
      : read.unread
        ? 'unread'
        : read.live
          ? 'live'
          : held
            ? 'held'
            : 'off';
  const word = {
    live: feedWord,
    held: 'Held',
    off: `${feedWord} off`,
    ondemand: 'On demand',
    unread: read.fetching ? 'Reading' : 'No reading',
    failing: 'Failing',
  }[state];
  const dotTone: Tone = {
    live: 'nominal',
    held: 'warn',
    off: 'warn',
    ondemand: 'neutral',
    /* Grey, not amber: nothing has failed. THE BAR §3 — a state the console
       has not measured yet is unknown, and spending a fault colour on it
       devalues the colour on the surface that has really lost its link. */
    unread: 'neutral',
    failing: 'error',
  }[state] as Tone;

  const surface = strip.surfaces.find((s) => s.key === strip.activeKey)?.label ?? strip.root.label;
  const holdTitle =
    state === 'failing'
      ? `Reads for ${surface} are failing. The age beside this is the last one that succeeded.`
      : state === 'ondemand'
        ? `${surface} is read on demand — nothing polls it, so there is nothing to hold`
        : held
          ? `${surface} is held: every poll in the console is stopped. Press to resume.`
          : `${surface} is refreshing on its own. Press to hold every poll in the console.`;

  return (
    <>
      <button
        type="button"
        className={styles.liveSwitch}
        data-state={state}
        aria-pressed={state === 'live'}
        aria-keyshortcuts="L"
        disabled={state === 'ondemand'}
        title={`${holdTitle}\n\nShortcut: L`}
        onClick={() => setHeld(!held)}
      >
        <StatusDot tone={dotTone} hollow={state !== 'live'} />
        {/* §6: the shortcut is printed in the affordance, in mono, at the dim
            step, and hard against the word it belongs to — a key floating in
            the middle of a control is a glyph nobody can attribute. `L` is the
            console's "go live" key. It sheds below 1440px, where the strip
            needs its width for the reading rather than for a keyboard note. */}
        <span className={styles.liveLabel}>
          <span className={styles.liveWord}>{word}</span>
          <kbd className={styles.liveKey} aria-hidden="true">
            L
          </kbd>
        </span>
      </button>

      <button
        type="button"
        className={styles.ageBtn}
        title={`${surface} — stalest source ${
          read.ageMs === null ? 'never read' : `read ${duration(read.ageMs)} ago`
        }.\n${read.detail}\nPress to re-read.`}
        /* Contains the visible string, so "click the read-3s-ago control"
           addresses the same thing by voice and by eye (WCAG 2.5.3). */
        aria-label={`${
          read.ageMs === null ? 'Never read' : `Read ${duration(read.ageMs)} ago`
        } — re-read ${surface} now`}
        onClick={read.reread}
      >
        <span className={styles.ageLead}>read</span>
        <span className={styles.ageValue} data-tone={read.tone} data-fetching={read.fetching}>
          {duration(read.ageMs, { compact: 1 })}
        </span>
        {/* `read — ago` is not a sentence. With no successful read behind it
            the em dash stands alone and says the whole thing. `ago` is also
            the only word in this cluster narrow enough to be worth shedding:
            `read 3s` still names what the number is. */}
        {read.ageMs !== null && <span className={styles.ageTrail}>ago</span>}
        <Icon name="refresh" size={11} />
      </button>
    </>
  );
}

function SurfaceStrip({
  strip,
  read,
  robots,
  mainRef,
  onNavigate,
}: {
  strip: Strip;
  read: SurfaceRead;
  robots: readonly Robot[];
  mainRef: React.RefObject<HTMLElement>;
  onNavigate: (path: string) => void;
}) {
  const qc = useQueryClient();
  const tick = useCacheTick(qc);
  /* The same query the top bar's queue control mounts, deduped by key — no
     second request, and the two bands cannot report different queues. */
  const pending = usePendingCommands();
  const stripRef = useRef<HTMLElement>(null);
  const slotRef = useRef<HTMLSpanElement>(null);
  const [hoistW, setHoistW] = useState(0);

  /* ---- the page's own control, moved into a zone of the strip -------------
     The page frame (components/SurfaceShell) renders a 40px identity band with
     an action slot in it. Under a strip the identity is redundant — the strip
     says it, better — and the action belongs in the strip. The band collapses
     in CSS; the action stays a live React node owned by the page (the shell
     has no way to drive page state) and is absolutely positioned into the
     strip. All this measurement does is reserve the space it will land in, so
     the two zones never overlap. Nothing found → nothing reserved. */
  useLayoutEffect(() => {
    const main = mainRef.current;
    if (!main) return;

    /* Defensively, not structurally. The page frame is a shared primitive and
       four other builders are editing the surfaces that use it, so the hoist
       asks two questions rather than counting children: does the page have a
       header, and does that header hold anything BESIDES its identity block?
       A header with one child is identity only and there is nothing to move.

       Deliberately not "the last child containing a button": /commands' action
       slot is a live SSE readout with no control in it, and a rule that clever
       reserved nothing for it and let it paint over the freshness cluster. The
       contract is positional — last block in the head — because that is the
       only thing SurfaceShell actually promises.

       AT EITHER DEPTH, AND THE MATCH IS MARKED RATHER THAN RE-DERIVED IN CSS.
       A page is entitled to wrap its frame in a `display: contents` element —
       /fleet does, to scope tokens — and that wrapper is invisible to layout
       but NOT to a child combinator. The collapse used to be written as
       `main > * > header` in the stylesheet, so /fleet, and only /fleet, kept
       a second 40px identity band: `FLEET · Operations · 28 robots · 3 sites ·
       4 morphologies · 5 agent builds` drawn 40px under a strip already
       reading `Fleet › Operations 13/28 … 28 hardware · 4 morphologies ·
       5 builds · 3 sites`, with a second copy of the page's verb beside it.

       So the header is found HERE, where the two candidate depths can be tried
       in document order (the surface's own head always precedes any panel's),
       and tagged. The stylesheet then addresses `header[data-shell-head]`
       rather than guessing a depth — which also means a deeper wrapper only
       ever costs this query, never a silently mis-targeted rule. */
    /* `[data-shell-canvas]` is a workspace that is ALL instrument (the signal
       explorer): it is not a page frame, it draws panels whose 28px headers
       are real, and a rule written for somebody else's identity band must not
       collapse them.
       The shell excluding itself is cheaper than teaching the selector what
       a Panel is. */
    const header = main.querySelector<HTMLElement>(
      ':scope > *:not(nav):not([data-shell-canvas]) > header,' +
        ' :scope > *:not(nav):not([data-shell-canvas]) > *:not(nav) > header',
    );
    if (header) header.dataset['shellHead'] = 'true';
    /* Cleanup and setup run in the same layout commit, so the attribute is
       removed and re-applied before the browser paints: a re-run on `tick`
       cannot flash the band. */
    const untag = () => {
      if (header) delete header.dataset['shellHead'];
    };

    if (strip.hoist === 'none') {
      setHoistW((w) => (w === 0 ? w : 0));
      return untag;
    }

    const blocks = header
      ? [...header.children].filter((c): c is HTMLElement => c instanceof HTMLElement)
      : [];
    const el = blocks.length > 1 ? (blocks[blocks.length - 1] ?? null) : null;
    const measure = () => {
      const w = el ? el.getBoundingClientRect().width : 0;
      setHoistW((prev) => (Math.abs(prev - w) < 0.5 ? prev : w));
      const slot = slotRef.current;
      if (slot) main.style.setProperty('--hoist-x', `${slot.offsetLeft}px`);
    };
    measure();
    if (!el) return untag;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const so = new ResizeObserver(measure);
    if (stripRef.current) so.observe(stripRef.current);
    return () => {
      ro.disconnect();
      so.disconnect();
      untag();
    };
    // `tick` re-runs the query so a control that mounts after the first paint
    // (a page still loading its robots) is picked up within a beat.
  }, [mainRef, strip.hoist, strip.activeKey, hoistW, tick]);

  /* ---- the object this surface is scoped to ------------------------------- */
  const scopeRobot = strip.objectId === null
    ? undefined
    : robots.find((r) => r.robot_id === strip.objectId);

  /* ---- the provenance line ------------------------------------------------ */
  const facts = useMemo<Fact[]>(() => {
    const n = robots.length;
    switch (strip.kind) {
      case 'fleet':
        return n === 0
          ? [{ value: NO_VALUE, label: 'robots' }]
          : [
              ...fleetSizeFacts(robots),
              {
                value: String(countDistinct(robots, (r) => r.morphology)),
                label: 'morphologies',
                abbr: 'morph',
              },
              /* §13 tell 23 — provenance that stops at the robot name is not
                 provenance. At fleet scale the software version is a FACT
                 about the fleet: five builds across 28 robots is a fleet
                 mid-rollout, one build is a fleet that is done. */
              {
                value: String(countDistinct(robots, (r) => r.firmware)),
                label: 'builds',
                abbr: 'bld',
              },
              {
                value: String(countDistinct(robots, (r) => metaString(r, 'site'))),
                label: 'sites',
              },
            ];
      case 'telemetry': {
        /* A robot-scoped surface answers §6's provenance list about the ROBOT,
           not about the fleet: which morphology, which agent build, and — the
           one that decides whether any of this is real — hardware or sim. All
           three are the API's own words off `/api/robots`.

           HOW MANY readings and over WHAT WINDOW used to be here too, and they
           are the floor's now: this band answers what KIND of thing the
           surface is drawing and where it came from, the band at the other
           edge of the screen answers how much of it there is and how current
           it is. Printing `100 readings · 3h 57m window` in both was the same
           two strings 950px apart in the same reading order. */
        const r = scopeRobot;
        const kind = r ? metaString(r, 'kind') : null;
        return [
          ...(r ? [{ value: r.morphology, label: 'morphology', abbr: 'morph' }] : []),
          ...(r ? [{ value: r.firmware ?? NO_VALUE, label: 'build', abbr: 'bld' }] : []),
          ...(kind ? [{ value: kind, label: '', token: true }] : []),
        ];
      }
      /* The queue's composition, in the API's own status words — the same two
         strings the table's STATUS column prints on every row, and they sum to
         the `16 unacked` the top bar carries off the same endpoint.

         `pending` and `sent` are not a formality: pending is an order the
         platform still holds, sent is one a robot has been handed and has not
         acknowledged. Sixteen pending is a dispatcher that has stopped;
         sixteen sent is a fleet that has. The table says which each ROW is and
         nothing said how many of each, which is §13 tell 19 — a queue whose
         state groups carry no counts.

         Deliberately not a third noun for the total: the bar says `unacked`,
         the /fleet panel says `Queue`, and those two already mean different
         sets. This band adds the split and no new word. */
      case 'commands': {
        const rows = pending.data;
        if (rows === undefined) return [{ value: NO_VALUE, label: 'unacked' }];
        let sent = 0;
        for (const c of rows) if (c.status === 'sent') sent += 1;
        return [
          { value: String(rows.length - sent), label: 'pending', title: 'Held by the platform — not yet handed to a robot' },
          { value: String(sent), label: 'sent', title: 'Handed to a robot and not yet acknowledged' },
        ];
      }
      /* One number, and it is the surface's VOLUME, which the floor carries.
         Zone 2 keeps the endpoint, which is the whole of what it can honestly
         add without a second read. */
      case 'catalog':
        return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strip.kind, robots, scopeRobot, qc, tick, pending.data]);

  const source =
    strip.objectScope === 'telemetry-robot' && strip.objectId !== null
      ? `/api/robots/${strip.objectId}/telemetry`
      : strip.source;

  /* ←/→ walk the tabs. Focus only — a surface change is a navigation and must
     stay a deliberate press, not a side effect of looking. */
  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    const tabs = Array.from(stripRef.current?.querySelectorAll<HTMLElement>('[data-tab]') ?? []);
    const i = tabs.indexOf(document.activeElement as HTMLElement);
    if (i < 0 || tabs.length === 0) return;
    e.preventDefault();
    const next =
      e.key === 'ArrowRight' ? (i + 1) % tabs.length : (i - 1 + tabs.length) % tabs.length;
    tabs[next]?.focus();
  }, []);

  const leaf = strip.surfaces.find((s) => s.key === strip.activeKey) ?? strip.surfaces[0];

  return (
    <nav className={styles.strip} aria-label={strip.aria} ref={stripRef} onKeyDown={onKeyDown}>
      {/* ---- IDENTITY ------------------------------------------------------ */}
      <div className={styles.zoneId}>
        <button
          type="button"
          className={styles.crumbRoot}
          onClick={() => onNavigate(strip.root.path)}
          title={`${strip.root.label} workspace`}
        >
          {strip.root.label}
        </button>
        <span className={styles.crumbSep} aria-hidden="true">
          ›
        </span>

        {strip.surfaces.length > 1 ? (
          <div className={styles.tabs}>
            {strip.surfaces.map((s) => {
              const active = s.key === strip.activeKey;
              return (
                <button
                  key={s.key}
                  type="button"
                  data-tab="true"
                  className={styles.tab}
                  data-active={active}
                  aria-current={active ? 'page' : undefined}
                  /* On the whole tab, not on the five characters of the count:
                     a tooltip you have to find is a tooltip nobody reads. */
                  title={s.countTitle || undefined}
                  onClick={() => onNavigate(s.path)}
                >
                  <span className={styles.tabLabel}>{s.label}</span>
                  {s.count !== null && (
                    /* Numerator lit, denominator dim — the same two-part
                       treatment the top bar's reading and the nav rail's cell
                       use, so `13/28` is character-for-character AND
                       colour-for-colour the same object in all three. */
                    <span
                      className={styles.tabCount}
                      data-tone={s.countTone ?? 'none'}
                      data-unread={s.count.includes(NO_VALUE)}
                    >
                      {s.countTone === undefined || !s.count.includes('/') ? (
                        s.count
                      ) : (
                        <>
                          <span className={styles.tabLead}>{s.count.slice(0, s.count.indexOf('/'))}</span>
                          {s.count.slice(s.count.indexOf('/'))}
                        </>
                      )}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        ) : (
          /* One surface, so nothing to switch between: a plain crumb leaf.
             An accent underline here would mark a choice that does not exist. */
          <span className={styles.leaf} aria-current="page">
            {leaf?.label}
          </span>
        )}

        {strip.objectScope !== null && (
          <>
            <span className={styles.crumbSep} aria-hidden="true">
              ›
            </span>
            {/* The object picker IS the crumb leaf: 13px/500 in --text-hi like
                the surface name beside it, no filled box, a hairline only on
                hover. Fixed width, sized to the longest name the fleet
                carries, so choosing a different robot cannot slide the
                provenance line packed against it. */}
            <span className={styles.picker}>
              <select
                className={styles.pickerSelect}
                aria-label="Robot"
                value={strip.objectId ?? ''}
                disabled={robots.length === 0}
                onChange={(e) => { onNavigate(buildPath('telemetry', e.target.value)); }}
              >
                {strip.objectId === null && <option value="">{NO_VALUE}</option>}
                {robots.map((r) => (
                  <option key={r.robot_id} value={r.robot_id}>
                    {r.name ?? r.robot_id}
                  </option>
                ))}
              </select>
              <Icon name="chevron-down" size={12} />
            </span>
            <span
              className={styles.objectId}
              title={
                scopeRobot === undefined
                  ? 'No robot selected'
                  : `Showing ${scopeRobot.name ?? scopeRobot.robot_id} · ${scopeRobot.robot_id}`
              }
            >
              {strip.objectId ?? NO_VALUE}
            </span>
          </>
        )}
      </div>

      {/* ---- PROVENANCE + FRESHNESS ---------------------------------------
          This band used to be REPLACED by the outage, which cost the surface
          its endpoint list and its read age at the one moment both are worth
          reading — and put a second Retry 26px above the banner's. The outage
          is stated once now, by `components/states`'s OutageBanner under this
          strip; zones 2 and 3 go on saying what this surface is drawn from and
          how current it is, and `SurfaceLive` goes on refusing to say `Live`
          over a read that is failing. */}
      <div
        className={styles.zoneSource}
        title={
          facts.length === 0
            ? `This surface is read from ${source}. How much of it there is, how often it re-reads and when it last answered are in the status bar, bottom right.`
            : `${facts
                .map((f) => `${f.value} ${f.label}`)
                .join(' · ')} — read from ${source}. How much of it there is, how often it re-reads and when it last answered are in the status bar, bottom right.`
        }
      >
        {/* A separator with nothing behind it is a rule the reader has to
            account for. Where the surface has no composition to report, zone 2
            is the endpoint alone and there is nothing to separate it from. */}
        {facts.length > 0 && <span className={styles.zoneSep} aria-hidden="true" />}
        {facts.map((f, i) => (
          <span key={`${i}:${f.label}:${f.value}`} className={styles.fact} data-token={f.token}>
            <span className={styles.factValue}>{f.value}</span>
            {/* Both spellings are in the DOM; the breakpoint picks one. The
                label narrows, it never leaves — a bare `28 4 5 3` is four
                numbers with nothing to say what they count. */}
            {f.label !== '' && (
              <>
                <span className={styles.factLong}>{f.label}</span>
                <span className={styles.factShort}>{f.abbr ?? f.label}</span>
              </>
            )}
          </span>
        ))}
        <span className={styles.sourcePath}>{source}</span>
      </div>

      {/* ---- FRESHNESS + VERB ---------------------------------------------- */}
      <div className={styles.zoneVerb}>
        <SurfaceLive strip={strip} read={read} />
        {strip.hoist === 'right' && (
          /* The page's own primary action lands here — see the hoist, above.
             The span only reserves the width the control measures; the control
             itself is the page's live node, positioned over it. */
          <span
            className={styles.verbSlot}
            ref={slotRef}
            style={{ width: `${Math.max(hoistW, 0)}px` }}
            aria-hidden="true"
          />
        )}
      </div>
    </nav>
  );
}

/* ==========================================================================
   THE FLOOR — a 22px band, on every route, carrying the DEPLOYMENT and
   nothing else.

   §6 of the bar asks for "ambient connection truth in a fixed corner" —
   `palantir-gotham-map-workspace.png` puts `Map health — Connected`
   bottom-left and never moves it — and until this round no route answered it.

   The floor answered it, and then kept going: it also printed
   `28 robots · 22 live · 2 faulted · 8 under 40% · 1 never reported · last
   read 09:06:43Z`. Every one of those was already on screen. `2 faulted` was
   in the top bar and in the `FAULTED 2/28` card; the read stamp was a fourth
   spelling of a freshness the top bar, the surface strip and every panel
   footer were each already stating. 535px of a 1600px band, repeating.

   So the console now assigns each fact exactly one band, and the floor's is
   the smallest and the most stable of the three:

     TOP BAR   the fleet rollup, the transport, the channel alarm, Zulu
     STRIP     this route, its endpoints, Live/Hold, and the read age
     FLOOR     the DEPLOYMENT — how data is meant to arrive, whose fleet this
               is, which host is serving it, and how often it is asked

   Everything on the floor changes when someone deploys, not when someone
   drives a robot. It is the one band an operator does not have to re-read.
   ========================================================================== */

/* --------------------------------------------------------------------------
   ONE FLOOR, NOT TWO.

   The shell now owns the band at the bottom of the screen, and /fleet/list
   still draws a status bar of its own at the foot of its page block — the
   same five counts, 24px above the shell's. Two hairline bands stacked at
   the bezel is not a status bar, it is a mistake, so the shell collapses the
   page's copy the same way it already collapses the page frame's identity
   header under the surface strip.

   Detected by SHAPE, never by class name — four other builders are editing
   these surfaces at the same time and a selector aimed at one of their
   modules would be a landmine. The signature is specific:

     · the last element child of the page's fill block,
     · with at least one earlier sibling,
     · 18–36px tall,                       (a band, not a region)
     · `flex-grow: 0`,                     (it is not the thing that fills)
     · following a sibling that DOES grow. (it caps a filling region)

   Nothing that is not a status bar has that shape: a table, a panel or a grid
   is either the growing child or hundreds of pixels tall. If a surface stops
   drawing one, the probe finds nothing and does nothing.

   What is lost by hiding it: the list's counts double as filters (`2 faulted`
   isolates the two rows). Those same facets are in the `Filters ⌄` menu 900px
   above with the same counts, so the capability survives the collapse — but
   the root fix is for the page to stop drawing the band and let the shell's
   slot carry it, and that is the first item in this piece's report.
   -------------------------------------------------------------------------- */
function findPageFoot(main: HTMLElement): HTMLElement | null {
  const body = main.querySelector<HTMLElement>(':scope > section > div:last-of-type');
  const fill = body?.firstElementChild;
  if (!(fill instanceof HTMLElement)) return null;
  const kids = [...fill.children].filter((c): c is HTMLElement => c instanceof HTMLElement);
  if (kids.length < 2) return null;
  const last = kids[kids.length - 1];
  if (!last) return null;
  const cs = getComputedStyle(last);
  if (parseFloat(cs.flexGrow) > 0) return null;
  /* THE PROBE MUST RECOGNISE ITS OWN HANDIWORK.

     The height test is the whole signature — "a band, not a region" — and it
     cannot be applied to an element this probe has already collapsed, because
     a collapsed band measures 0. The first version did exactly that and
     oscillated on a one-second cycle: detect (18–36px) → hide → re-measure
     (0px) → un-detect → show → detect… The page's status bar flickered in and
     out at 1Hz under the shell's own, and which of the two states a
     screenshot caught was a coin toss.

     A `display: none` last child that still caps a filling sibling is the
     band we hid on the previous pass. Nothing else can be in that position:
     if the surface genuinely stopped drawing one, the element is gone and the
     probe returns null on the shape test above. */
  if (cs.display !== 'none') {
    const h = last.getBoundingClientRect().height;
    if (h < 18 || h > 36) return null;
  }
  const capsAFiller = kids
    .slice(0, -1)
    .some((k) => parseFloat(getComputedStyle(k).flexGrow) > 0);
  return capsAFiller ? last : null;
}

function everyShort(ms: number | null): string {
  if (ms === null) return 'on demand';
  return ms % 1000 === 0 ? `${ms / 1000}s` : `${(ms / 1000).toFixed(1)}s`;
}

function StatusBar({ read }: { read: SurfaceRead }) {
  const supply = useSupply();
  const { data: auth } = useAuth();

  /* Both of these are true whatever the platform is doing — the host comes off
     the address bar, and the tenant is the last one the API named. A corner
     that goes blank when the API dies is a corner that is missing at the exact
     moment an operator is trying to work out whether the problem is theirs. */
  const tenant = auth?.user?.tenant ?? NO_VALUE;
  const host = typeof window === 'undefined' ? NO_VALUE : window.location.host;

  return (
    <footer className={styles.status} role="contentinfo" aria-label="Console status">
      {/* The one thing on this band worth interrupting a screen-reader
          operator for, and the only thing on it that changes rarely enough to
          be worth announcing: whether data is arriving at all. */}
      <span className={styles.srOnly} role="status" aria-live="polite">
        {`Data supply: ${supply.phrase}`}
      </span>

      <div className={styles.statusLeft}>
        {/* The plane, in a sentence rather than in the four glyphs the 34px
            band has room for. The top bar's pill is the INSTRUMENT — a word
            you read by shape, with a meter and a median beside it; this is
            the ambient corner §6 asks for, and it is the same computation,
            published through TopBar's supply store, so the two corners of the
            screen cannot disagree. */}
        <span
          className={styles.link}
          data-state={supply.state}
          title={
            supply.state === 'down'
              ? 'Reads are failing — nothing is arriving on either plane'
              : supply.state === 'held'
                ? 'Every poll in the console is held by the operator. Press L to resume.'
                : supply.state === 'live'
                  ? 'MQTT push is connected — readings arrive when the robot sends them'
                  : 'Push is unavailable; REST polling is carrying the console'
          }
        >
          <StatusDot tone={supply.tone} hollow={supply.state !== 'live'} />
          <span className={styles.linkPhrase}>{supply.phrase}</span>
        </span>

        <span className={styles.statusSep} aria-hidden="true" />
        <span className={styles.statusMono} title={`Tenant: ${tenant}`}>
          {tenant}
        </span>

        <span className={styles.statusSep} aria-hidden="true" />
        <span className={styles.statusMono} title={`App and API are served from ${host}`}>
          {host}
        </span>

        <span className={styles.statusSep} aria-hidden="true" />
        {/* The CADENCE, not the age. How often the console asks is a property
            of the build; how stale the answer is belongs to the surface strip,
            which states it in the scope the operator is looking at and puts a
            re-read verb on it. */}
        <span
          className={styles.statusPair}
          title={
            read.everyMs === null
              ? 'Nothing on this surface polls — it is read on demand'
              : `The slowest source behind this surface re-reads every ${everyShort(read.everyMs)}`
          }
        >
          <span className={styles.statusLabelLead}>
            {read.everyMs === null ? 'read' : 'every'}
          </span>
          <span className={styles.statusValue}>{everyShort(read.everyMs)}</span>
        </span>
      </div>
    </footer>
  );
}

/* ========================================================================== */


export function AppShell() {
  const { path, setPath } = useBrowserRoute();
  const { workspace, segments } = parsePath(path);
  const { data } = useRobots();
  const mainRef = useRef<HTMLElement>(null);
  const { held, setHeld } = useLiveHold();
  const robots = useMemo(() => data ?? [], [data]);
  /* Undefined covers both "not read yet" and "the read failed" — in either
     case the console does not know the fleet and must not print a number. */
  const unread = data === undefined;

  /* `segments` is a fresh array every parse; its content is what matters. */
  const route = segments.join('/');

  const strip = useMemo(
    () => stripFor(workspace, segments, robots, unread),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workspace, route, robots, unread],
  );

  const active = strip?.surfaces.find((s) => s.key === strip.activeKey);
  const title = active?.doc ?? fallbackTitle(workspace, segments, robots);

  /* The same function the strip's picker and endpoint read, so the crumb, the
     endpoint and the canvas are one robot. */
  const telemetryRobot = useMemo(
    () => (workspace === 'telemetry' ? telemetryRobotOf(segments, robots) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workspace, route, robots],
  );

  /* Read ONCE, here, and handed down. The strip prints it, the canvas is
     gated on it, and <main> carries it as an attribute — three consumers of
     one computation, so the band and the workspace under it can never
     disagree about whether the platform is answering.

     A surface with no strip (a robot's own detail page, a scan viewer) still
     hangs off the fleet read, so it is gated on the same fact. */
  const sourceIds = useMemo<readonly string[]>(
    () => strip?.sourceIds ?? (workspace === 'fleet' ? ['robots'] : []),
    [strip, workspace],
  );
  const read = useSurfaceRead(sourceIds);

  useEffect(() => {
    document.title = `${title} · F3 Fleet`;
  }, [title]);

  /* §6's `L` — go live. The console has exactly two global keys and this is
     the second: it flips the hold that every poll in the build is gated on, so
     an operator reading row 40 can stop the world and start it again without
     reaching for the pointer. Bare, so it is gated on nobody typing; the same
     `isTypingTarget` the palette's `/` uses, because two definitions of "is
     this a text field" would eventually disagree and eat a letter. */
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'l' && e.key !== 'L') return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      e.preventDefault();
      setHeld(!held);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [held, setHeld]);

  const navigate = useCallback((next: string) => setPath(next), [setPath]);

  /* `L` is a bare keystroke that changes the state of every panel on screen,
     and the four places it shows — the mode pill, the strip's switch, the
     panel chips, the floor — are all VISIBLE changes. So the flip is spoken
     once, here, rather than left to whichever of the four an operator's
     cursor happens to be near. Skipped on mount: nothing has changed yet, and
     a console that announces its own resting state on load is noise. */
  const [spoken, setSpoken] = useState('');
  const heldWas = useRef(held);
  useEffect(() => {
    if (heldWas.current === held) return;
    heldWas.current = held;
    setSpoken(
      held
        ? 'Polling held. Every panel is showing its last reading.'
        : 'Polling resumed.',
    );
  }, [held]);

  /* One floor, not two — see findPageFoot. Measured after every paint the
     route or the cache could have changed, and re-measured when the canvas
     resizes, because the page's own band only exists once its data has
     landed. */
  const [pageFoot, setPageFoot] = useState(false);
  useLayoutEffect(() => {
    const main = mainRef.current;
    if (!main) return;
    const measure = () => {
      setPageFoot(findPageFoot(main) !== null);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(main);
    const t = window.setInterval(measure, 1_000);
    return () => {
      ro.disconnect();
      window.clearInterval(t);
    };
  }, [workspace, route]);

  let canvas: React.ReactNode;
  switch (workspace) {
    case 'fleet':
      canvas = <FleetWorkspace />;
      break;
    case 'telemetry':
      canvas = <TelemetryWorkspace robotId={telemetryRobot} robots={robots} />;
      break;
    case 'commands':
      canvas = <CommandsWorkspace />;
      break;
    case 'catalog':
      canvas = <CatalogWorkspace />;
      break;
  }

  return (
    <div className={styles.shell}>
      {/* First tab stop in the document: past the chrome, straight into the
          data. Visible only when focused. */}
      <a className={styles.skip} href="#canvas">
        Skip to content
      </a>
      <span className={styles.srOnly} role="status" aria-live="polite">
        {spoken}
      </span>
      <TopBar />
      <NavRail current={workspace} view={segments[0] ?? ''} onNavigate={navigate} />
      <main
        id="canvas"
        className={styles.main}
        ref={mainRef}
        tabIndex={-1}
        /* Propagated so a workspace can answer the same fact in its own
           terms. `data-failing` NAMES the source, because at panel scope that
           is the whole question: on /fleet with `/api/stats` dead and
           `/api/robots` alive, the status matrix is a true picture and the
           status distribution beside it is five zeroes about a fleet nobody
           counted. Only the page knows which of its panels is drawn from
           which — and every panel now answers it through
           `components/states`'s ReadPanel rather than through this attribute. */
        {...(read.fault !== null ? { 'data-failing': read.fault.label } : {})}
        {...(strip ? { 'data-strip': 'true', 'data-hoist': strip.hoist } : {})}
        {...(pageFoot ? { 'data-pagefoot': 'true' } : {})}
        /* THE HOLD, PROPAGATED. The console's transport state, published on
           the canvas so every panel inside it answers freshness in its own
           header rather than deferring to a light in the ceiling — BAR §6:
           "the operator must never have to ask 'is this now, and is this
           real?' Both answers are on screen at all times, PER PANEL."
           Consumed by AppShell.module.css and by any surface that wants to
           read it directly. */
        data-poll={held ? 'held' : 'live'}
      >
        {strip && (
          <SurfaceStrip
            strip={strip}
            read={read}
            robots={robots}
            mainRef={mainRef}
            onNavigate={navigate}
          />
        )}
        {/* A FAILED READ NEVER REPLACES THE ROUTE.

            The canvas used to be COVERED, on a cold start against a dead API,
            by a page of diagnostics: the operator lost the breadcrumb, the tab
            strip, the identity block, the column headings and every reserved
            cell, and got 46% of a 1600×1000 viewport of bare charcoal in
            exchange. It is the same event as a refresh failing over data that
            is already on screen, which this console has always handled by
            keeping the figures and marking them — two designs for one fact,
            and an operator had to learn both.

            `components/states`'s Surface is the one design: the route draws at
            full width in every state, a 26px verdict line goes above it and
            the 196px supply band — sources · console · retry policy · read
            attempts, every figure of it measured by this browser rather than
            reported by the API that is down — goes under it. Both appear the
            moment a source stops being supplied and vanish the moment none
            is. */}
        <Surface surface={active?.doc ?? title}>{canvas}</Surface>
      </main>
      {/* The floor. Outside <main>, so it is the shell's own band on every
          route rather than something a workspace can scroll away or forget to
          draw — see THE FLOOR, above. */}
      <StatusBar read={read} />
    </div>
  );
}
